import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { get_deployment, usePost } from "./API";
import { removeDuplicateEvents, useAsyncInterval, useInterval } from "./Util";
import { useMultipartUpload } from "./utils/useMultipartUpload";
import { ChangeSet, State, UserActivity } from "@aidkitorg/typesheets/lib/collab";
import { ConfigurationContext, UserInfoContext } from "./Context";
import { useChannel } from "./Realtime";
import { useToast } from "@aidkitorg/component-library";
import { compress, decompress } from "./utils/compress";
import { v4 as uuidv4 } from 'uuid';
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { BasePubSubEvent } from "@aidkitorg/types/lib/pubsub";
import { resolveCollabEvents as resolveRemoteCollabEvents } from "./collabRemote";
import { type VersionAction, type VersionSource } from "@aidkitorg/types/lib/survey";



export type DistroPartialChangeEvent = BasePubSubEvent<
  'partial_change',
  {
    compressedChunk: string,
    totalChunks: number,
    userActivity?: UserActivity,
    uuid: string
  }
>;

export type DistroChangeEvent = BasePubSubEvent<
  'change',
  {
    events: ChangeSet,
    userActivity?: UserActivity,
    uuid: string
  }
>;

export type DistroEnableCollabEvent = BasePubSubEvent<
  'enable_collab',
  {
    tabId: string,
    uid?: string,
    tags?: string[],
    refreshContext?: (() => void),
  }
>;

export type DistroDisableCollabEvent = BasePubSubEvent<
  'disable_collab',
  {
    tabId: string,
    uid?: string,
    tags?: string[],
    refreshContext?: (() => void),
  }
>;

export type DistroSavedSurveyEvent = BasePubSubEvent<
  'saved_survey',
  {
    username?: string,
    tabId: string,
  }
>;

export type DistroRemoveUserEvent = BasePubSubEvent<
  'remove_user',
  {
    name: string,
    browserTab: string,
  }
>;

export type CollabBusyState = 'switching' | 'rebasing' | 'resetting';

export const CollabBusyLabel: Record<CollabBusyState, string> = {
  rebasing: 'Syncing...',
  resetting: 'Resetting...',
  switching: 'Switching branch...',
};

export type ConfigRealtimeEvents =
    DistroPartialChangeEvent
    | DistroChangeEvent
    | DistroEnableCollabEvent
    | DistroDisableCollabEvent
    | DistroSavedSurveyEvent
    | DistroRemoveUserEvent;

export interface DistroBranch {
  id: number;
  name: string;
  created_at: string;
  created_by: string | null;
  parent_ops_version: string | null;
  is_dev_branch: boolean;
}

export function useCollabActions({
  surveyName,
  tabId,
  mergeReceivedEventsCallback,
  autosave = true,
  onSocketDisconnected,
  onSocketConnected,
  initialBranchId,
  source,
  paused = false,
  onRemoteCollabToggle,
  skipSelfEchoedEvents = false,
}: {
  surveyName: string,
  tabId: string,
  mergeReceivedEventsCallback: (events: ChangeSet, userActivity?: UserActivity[], initialize?: boolean, debug?: boolean, unpublishedEvents?: ChangeSet, collabEnabled?: boolean) => void,
  autosave?: boolean,
  onSocketDisconnected?: () => void,
  onSocketConnected?: () => void,
  initialBranchId?: number,
  source: VersionSource,
  /** When true (a mounted-but-hidden editor tab), close the websocket and skip the background
   *  polling/autosave. The caller resyncs on reactivation. Keeps hidden tabs cheap and quiet. */
  paused?: boolean,
  /** Called when another user/tab enables or disables collab on this survey. The tabbed config
   *  page uses this to remount just the affected editor — a full location.reload() (the fallback
   *  when this is not provided) would also destroy sibling tabs' unsaved in-memory edits. */
  onRemoteCollabToggle?: () => void,
  // Only safe when the caller already applies its own edits to local state before sending them
  // (e.g. the Distro editor's applyTransaction), making the self-echoed 'change'/'partial_change'
  // event a true no-op. Callers whose mergeReceivedEventsCallback is the ONLY path that ever
  // applies their own edits locally (e.g. CopyEditor) must leave this false.
  skipSelfEchoedEvents?: boolean,
}) {
  const user = useContext(UserInfoContext);
  const config = useContext(ConfigurationContext);
  const [channel, setChannel] = useState('');
  const [switchingBranch, setSwitchingBranch] = useState(false);
  const switchingBranchRef = useRef(false);
  const [rebasing, setRebasing] = useState(false);
  const rebasingRef = useRef(false);
  const [resetting, setResetting] = useState(false);
  const resettingRef = useRef(false);
  const fetchingBranchRef = useRef(false);
  const contextOperationInProgressRef = useRef(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const hasCollabBeenConnected = useRef(false);
  const disconnectToastTimeout = useRef<NodeJS.Timeout | null>(null);
  const disconnectToastDismiss = useRef<(() => void) | null>(null);

  const isOperationInProgress = useCallback(() =>
    switchingBranchRef.current ||
    rebasingRef.current ||
    resettingRef.current ||
    fetchingBranchRef.current ||
    contextOperationInProgressRef.current
  , []);

  const setContextOperationInProgress = useCallback((val: boolean) => {
    contextOperationInProgressRef.current = val;
  }, []);

  // Cleanup disconnect toast on unmount
  useEffect(() => {
    return () => {
      if (disconnectToastTimeout.current) clearTimeout(disconnectToastTimeout.current);
      disconnectToastDismiss.current?.();
    };
  }, []);
  // triggers the backend to autosave a collab version - ie, merge the dynamo events with 
  // the current s3 collab version, prune them to smallest necessary, and save that as the new version.
  const autoSaveCollabVersion = usePost('/survey/trigger_collab_autosave', { noRetry: true });
  // get collab events and settings
  const getCollabEvents = usePost('/survey/get_collab_events');
  const getCollabEnabled = usePost('/survey/get_collab_enabled');
  const getPublishedChanges = usePost('/survey/get_published_changes');
  // disable collab
  const clearCollabEvents = usePost('/survey/clear_collab_events');
  // save events from the client as a new collab version. Used for very large collections of changes
  // (like when someone copies and pastes something into the distro).
  const writeCollabVersion = usePost('/survey/write_collab_version', { compressRequestPayload: true, noRetry: true });
  // enable collab from a raw survey JSON — bootstraps the base ops and dev branch
  const enableCollabReq = usePost('/survey/enable_collab', { compressRequestPayload: true, noRetry: true });
  const getBranches = usePost('/survey/get_branches');
  const postCreateBranch = usePost('/survey/create_branch', { noRetry: true });
  const postRebaseBranch = usePost('/survey/rebase_branch', { noRetry: true });
  const postCheckBehindPublished = usePost('/survey/check_branch_behind_published');
  const postDeleteBranch = usePost('/survey/delete_branch', { noRetry: true });
  const postResetBranchToPublished = usePost('/survey/reset_branch_to_published', { noRetry: true });
  const postRestoreBranchToCollabVersion = usePost('/survey/restore_branch_to_collab_version', { noRetry: true });
  const deleteUserActivityForBranch = usePost('/survey/delete_user_activity_for_branch');

  const multipartUpload = useMultipartUpload();

  // Save LOCAL Collab events to Dynamo
  const saveCollabEvents = usePost('/survey/save_collab_events', { compressRequestPayload: true, keepAlive: true });
  // Your fresh Collab changes that still need to be persisted to Dynamo
  const eventsToSave = useRef<ChangeSet>();
  const saveEventsTimer = useRef<number | undefined>(undefined);
  // Retry timer for failed saves (cancelled if user makes a new change)
  const saveRetryTimer = useRef<number | undefined>(undefined);
  // Track consecutive save failures - alert user after 3 failures
  const consecutiveSaveFailures = useRef<number>(0);

  // Your fresh Collab changes that still need to be shared over Websockets
  const eventsToSend = useRef<ChangeSet>();
  const sendEventsTimer = useRef<number | undefined>(undefined);
  // Retry timer for failed WebSocket sends (cancelled if user makes a new change)
  const sendRetryTimer = useRef<number | undefined>(undefined);

  // Chunks of very large Collab change that are in the process of being received.
  // After final chunk is received, we merge events/state in Distro and this ref is cleared.
  // See: partial_change below
  const eventsAwaitingMerge = useRef<Record<string, ChangeSet[]>>({});

  // counts 3 seconds after the last incoming edit, to re-enable publishing.
  const incomingEditsTimer = useRef<number | undefined>(undefined);

  const [collabEnabled, setCollabEnabled] = useState(false);
  // Signals that the collab check has completed (regardless of whether collab is enabled).
  // Callers should wait for this before initializing the editor to avoid a double-init "blip".
  const [collabReady, setCollabReady] = useState(false);
  const [receivingEvents, setReceivingEvents] = useState(false);
  const [sendingEvents, setSendingEvents] = useState(false);
  const canPublish = !receivingEvents && !sendingEvents;
  const [branchId, setBranchId] = useState<number | undefined>();
  const branchIdRef = useRef(branchId);
  branchIdRef.current = branchId;
  const [branches, setBranches] = useState<DistroBranch[] | undefined>(undefined);
  const parentStateRef = useRef<State | undefined>(undefined);
  const parentOpsRef = useRef<ChangeSet>([]);
  const [behindPublished, setBehindPublished] = useState(false);

  const [debug, setDebug] = useState(false);

  const { toast } = useToast();

  useAsyncEffect(async () => {
    const [isCollabEnabled, branches] = await Promise.all([
      getCollabEnabled({ name: surveyName }),
      getBranches({ survey: surveyName }),
    ]);

    setCollabEnabled(isCollabEnabled);

    if (!isCollabEnabled) {
      setChannel(get_deployment() + ":" + surveyName);
      setCollabReady(true);
      return;
    }

    // For now, assume we're always on the dev branch
    const devBranch = branches.find(({ is_dev_branch }) => is_dev_branch);
    const requestedBranch = initialBranchId && branches.find(b => b.id === initialBranchId);
    const startBranch = requestedBranch || devBranch;

    if (!startBranch) {
      throw new Error('Could not find dev branch');
    }

    setBranchId(startBranch.id);
    setBranches(branches);
    setChannel(get_deployment() + ":" + surveyName + ":" + startBranch.id);
    setCollabReady(true);
  }, [surveyName]);

  // Resolves the full event list from a collabResp, populating parentStateRef as a side effect.
  // The fetch-and-assemble logic (and its ordering invariant) lives in collabRemote.ts so
  // headless readers can share it; this wrapper just keeps the refs.
  const resolveCollabEvents = useCallback(async (collabResp: Awaited<ReturnType<typeof getCollabEvents>>): Promise<{ events: ChangeSet, unpublishedEvents: ChangeSet }> => {
    parentStateRef.current = undefined;
    parentOpsRef.current = [];
    const { events, unpublishedEvents, parentOps, parentState } = await resolveRemoteCollabEvents(collabResp);
    parentStateRef.current = parentState;
    parentOpsRef.current = parentOps;
    return { events, unpublishedEvents };
  }, []);

  const getCollabState = useCallback(async () => {
    if (!collabEnabled) {
      const emptyActivity: Record<string, never> = {};
      return {
        status: 'ok',
        collabEnabled: false,
        userActivity: emptyActivity,
        events: [],
        savedVersionURL: '',
        debug: false,
        unpublishedEvents: [],
      };
    }

    if (!branchId) {
      throw new Error("Could not fetch collab state due to missing branch id");
    }

    const collabResp = await getCollabEvents({ name: surveyName, branchId });
    if (collabResp && collabResp.collabEnabled) {
      setCollabEnabled(true);
      setDebug(collabResp.debug);
      const { events, unpublishedEvents } = await resolveCollabEvents(collabResp);
      return {
        ...collabResp,
        events,
        unpublishedEvents
      };
    } else {
      // This seems like it is quietly turning off collab on the FE, even when it may still be on on the BE.
      // setCollabEnabled(false);
      return { ...collabResp, unpublishedEvents: [] as ChangeSet };
    }
  }, [surveyName, branchId, getCollabEvents, resolveCollabEvents]);

  // Check if the current branch is behind the published version
  const checkBehindPublished = useCallback(async () => {
    const currentBranchId = branchIdRef.current;
    if (!currentBranchId || !surveyName) return;
    const result = await postCheckBehindPublished({ survey: surveyName, branchId: currentBranchId });
    if (result && 'behindPublished' in result && branchIdRef.current === currentBranchId) {
      setBehindPublished(!!result.behindPublished);
    }
  }, [surveyName, postCheckBehindPublished]);

  const fetchPublishedChanges = useCallback(async () => {
    if (!branchId) return undefined;
    return await getPublishedChanges({ survey: surveyName, branchId });
  }, [branchId, getPublishedChanges, surveyName]);

  const eventCallback = useCallback(async (realtimeEvent: ConfigRealtimeEvents) => {
    // The pubsub server routes messages per registered channel, so cross-channel delivery
    // should never happen — but with multiple editors mounted on one page (survey tabs), a
    // misrouted event would be merged into the wrong survey's document. Drop anything that
    // declares a different channel. (Also catches late messages from a closing socket after
    // a branch switch, whose payloads carry the old channel.)
    if (realtimeEvent.channel && realtimeEvent.channel !== channel) {
      console.warn('Ignoring realtime event for channel', realtimeEvent.channel, 'while on', channel);
      return;
    }
    switch(realtimeEvent.event) {
      case 'enable_collab': // Fall through
      case 'disable_collab':
        if (user.uid !== realtimeEvent.data.uid || tabId !== realtimeEvent.data.tabId) {
          if (onRemoteCollabToggle) {
            onRemoteCollabToggle();
          } else {
            location.reload();
          }
        }
        break;
      case 'saved_survey':
        if (realtimeEvent.data.username !== config?.user?.name || realtimeEvent.data.tabId !== tabId) {
          const collabResp = await getCollabState();
          const userActivity = Object.keys(collabResp.userActivity || {})
            .map(k => {
              return {
                lastEditedObject: collabResp.userActivity[k].lastEditedObject,
                uid: collabResp.userActivity[k].uid,
                name: collabResp.userActivity[k].name,
                tabId: collabResp.userActivity[k].tabId
              }
            });
          mergeReceivedEventsCallback(collabResp.events, userActivity, true, collabResp.debug, collabResp.unpublishedEvents, !!collabResp.collabEnabled);
          toast({
            description: `${realtimeEvent.data.username} just published!`,
            variant: 'success'
          })
        }
        checkBehindPublished();
        break;
      case 'change': {
        const isOtherUser = realtimeEvent.data.userActivity?.uid !== user.uid || realtimeEvent.data.userActivity?.tabId !== tabId;
        if (realtimeEvent.data.events?.length && isOtherUser) {
          setReceivingEvents(true);
          if (incomingEditsTimer.current) clearTimeout(incomingEditsTimer.current);
          incomingEditsTimer.current = window.setTimeout(() => {
            setReceivingEvents(false);
          }, 3000);
        }

        if (isOtherUser || !skipSelfEchoedEvents) {
          console.log("Merging", realtimeEvent.data.uuid, realtimeEvent.data.events.length, realtimeEvent.data.userActivity);
          if (debug) {
            console.log('got events', JSON.stringify(realtimeEvent.data.events));
          }
          const unpublishedEvents: ChangeSet = isOtherUser ? realtimeEvent.data.events : [];
          mergeReceivedEventsCallback(realtimeEvent.data.events, realtimeEvent.data.userActivity ? [realtimeEvent.data.userActivity] : undefined, undefined, undefined, unpublishedEvents);
        }
        break;
      }
      case 'partial_change': {
        const isOtherUser = realtimeEvent.data.userActivity?.uid !== user.uid || realtimeEvent.data.userActivity?.tabId !== tabId;
        if (realtimeEvent.data.compressedChunk?.length && isOtherUser) {
          setReceivingEvents(true);
          if (incomingEditsTimer.current) clearTimeout(incomingEditsTimer.current);
          incomingEditsTimer.current = window.setTimeout(() => {
            setReceivingEvents(false);
          }, 3000);
        }

        const currentlyCollected = eventsAwaitingMerge.current[realtimeEvent.data.uuid] || [];
        const decompressedEvents = decompress(realtimeEvent.data.compressedChunk) as ChangeSet;
        eventsAwaitingMerge.current[realtimeEvent.data.uuid] = [...currentlyCollected, decompressedEvents];

        // When all chunks have come in, we can go ahead and merge.
        if (eventsAwaitingMerge.current[realtimeEvent.data.uuid]?.length === realtimeEvent.data.totalChunks) {
          if (isOtherUser || !skipSelfEchoedEvents) {
            const flattenedEvents = eventsAwaitingMerge.current[realtimeEvent.data.uuid].reduce((acc, value) => acc.concat(value), []);
            console.log("Merging chunked changes", realtimeEvent.data.uuid, flattenedEvents.length, realtimeEvent.data.userActivity);
            mergeReceivedEventsCallback(flattenedEvents, realtimeEvent.data.userActivity ? [realtimeEvent.data.userActivity] : undefined);
          }

          eventsAwaitingMerge.current[realtimeEvent.data.uuid] = [];
        }
        break;
      }
      default: {
        // Ignore unexpected event
        return;
      }
    }
  }, [config?.user?.name, toast, user.uid, mergeReceivedEventsCallback, getCollabState, tabId, debug, checkBehindPublished, channel, onRemoteCollabToggle, skipSelfEchoedEvents]);
  
  const handleSocketDisconnected = useCallback(() => {
    setSocketConnected(false);
    onSocketDisconnected?.();
    // Show disconnect toast internally only when the caller has no custom handler
    if (!onSocketDisconnected) {
      if (disconnectToastTimeout.current) clearTimeout(disconnectToastTimeout.current);
      disconnectToastTimeout.current = setTimeout(() => {
        disconnectToastDismiss.current?.();
        const { dismiss } = toast({
          description: 'Websocket disconnected. Collaborative editing may be unavailable. Attempting to reconnect...',
          variant: 'error',
          duration: undefined,
        });
        disconnectToastDismiss.current = dismiss;
        disconnectToastTimeout.current = null;
      }, 2000);
    }
  }, [onSocketDisconnected, toast]);

  const sendEvent = useChannel<ConfigRealtimeEvents>({
    realm: 'distro',
    name: channel,
    disabled: paused,
    onMessage: eventCallback,
    onOpen: () => {
      setSocketConnected(true);
      onSocketConnected?.();
    },
    onClose: handleSocketDisconnected,
    onError: handleSocketDisconnected,
  });

  // Check behind-published on initial load and branch switch
  useEffect(() => {
    if (collabEnabled && branchId) checkBehindPublished();
  }, [branchId, collabEnabled, checkBehindPublished]);

  const getCollabStateRef = useRef(getCollabState);
  // Use a ref so the reconnect effect doesn't re-fire when the inline callback identity changes
  const mergeReceivedEventsCallbackRef = useRef(mergeReceivedEventsCallback);
  mergeReceivedEventsCallbackRef.current = mergeReceivedEventsCallback;

  // Refetch collab state on WebSocket reconnect to catch any missed events
  // (skip initial connect - we already fetch on page load)
  useEffect(() => {
    let shouldShowReconnectedToast = true;
    // When the UI language changes, a cascade of React dependencies causes getCollabState to update
    // and causes us to toast that the websocket reconnected multiple times. This is unnecessary so
    // explicitly skip toasting when getCollabState changed.
    if (getCollabStateRef.current !== getCollabState) {
      getCollabStateRef.current = getCollabState;
      shouldShowReconnectedToast = false;
    }

    if (socketConnected && collabEnabled && hasCollabBeenConnected.current) {
      if (disconnectToastTimeout.current) {
        clearTimeout(disconnectToastTimeout.current);
        disconnectToastTimeout.current = null;
      }
      if (disconnectToastDismiss.current) {
        disconnectToastDismiss.current();
        disconnectToastDismiss.current = null;
      }
      if (shouldShowReconnectedToast) {
        toast({
          description: 'Websocket reconnected. Syncing latest changes...',
          variant: 'success',
          duration: 3000,
        });
      }
      (async () => {
        const collabResp = await getCollabState();
        if (!collabResp || (collabResp as any).error) {
          toast({
            description: 'Failed to sync latest changes. Please refresh the page before making edits.',
            variant: 'error',
          });
          return;
        }
        if (collabResp.events) {
          mergeReceivedEventsCallbackRef.current(collabResp.events, undefined, undefined, undefined, collabResp.unpublishedEvents);
        }
      })();
    }
    if (socketConnected && collabEnabled) {
      hasCollabBeenConnected.current = true;
    }
  }, [socketConnected, collabEnabled, getCollabState, toast]);

  const alertJustPublished = useCallback(async () => {
    sendEvent({
      realm: 'distro',
      channel,
      event: 'saved_survey',
      data: {
        username: config?.user?.name,
        tabId
      }
    });
  }, [sendEvent, config?.user?.name, tabId, channel]);

  // Broadcasts events to other editors via WebSocket for real-time sync.
  // Note: WebSocket sends are fire-and-forget - we can detect if the socket is closed or if send throws,
  // but not if messages are silently lost in transit. DynamoDB is the source of truth;
  const doSendRealtimeEvents = useCallback(async (myActivity?: UserActivity, uuid1?: string) => {
    const eventsSnapshot = eventsToSend.current;
    const toSend = removeDuplicateEvents(eventsSnapshot || []);
    if (toSend.length === 0) {
      return;
    }
    console.log('Sending events', uuid1, JSON.stringify(toSend));
    // We have a 32,768 byte (32 KB) limit on our production WebSocket API (AWS API Gateway).
    // If we have more than 100 events to send, let's split and compress them.
    let sendFailed = false;
    if (toSend.length > 100) {
      // Max compressed size is 30KB to leave room for the WebSocket message wrapper (~1-2KB)
      const MAX_COMPRESSED_SIZE = 30000;
      // Initial chunk size - 700 events typically compress to 17-26KB which fits under the limit.
      // Dynamic splitting handles any chunks that still exceed the limit after compression.
      const INITIAL_CHUNK_SIZE = 700;

      let hadOversizedEvent = false;

      // Split events that exceed the size limit after compression
      const splitIfNeededAndCompress = (events: ChangeSet): { events: ChangeSet, compressed: string }[] => {
        const compressed = compress(events);
        if (compressed.length <= MAX_COMPRESSED_SIZE) {
          return [{ events, compressed }];
        }
        // Cannot split a single event further - would cause infinite recursion
        if (events.length === 1) {
          hadOversizedEvent = true;
          return [{ events, compressed }];
        }
        // Split in half and recurse
        const mid = Math.floor(events.length / 2);
        const left = events.slice(0, mid);
        const right = events.slice(mid);
        return [...splitIfNeededAndCompress(left), ...splitIfNeededAndCompress(right)];
      };

      // First, create initial chunks of INITIAL_CHUNK_SIZE to avoid compressing huge data sets
      const initialChunks: ChangeSet[] = [];
      for (let i = 0; i < toSend.length; i += INITIAL_CHUNK_SIZE) {
        initialChunks.push(toSend.slice(i, i + INITIAL_CHUNK_SIZE));
      }

      // Then, compress each chunk and split further only if needed
      const chunks = initialChunks.flatMap(chunk => splitIfNeededAndCompress(chunk));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const { compressed: compressedChunk } = chunks[chunkIndex];
        const result = sendEvent({
          realm: 'distro',
          channel,
          event: 'partial_change',
          data: {
            compressedChunk: compressedChunk,
            totalChunks: chunks.length,
            userActivity: myActivity,
            uuid: uuid1 || ''
          }
        });
        if (result?.status !== 'success') {
          const sentEvents = chunks.slice(0, chunkIndex).flatMap(c => c.events);
          if (sentEvents.length > 0 && eventsToSend.current) {
            const sent = new Set(sentEvents);
            const remaining = eventsToSend.current.filter(e => !sent.has(e));
            eventsToSend.current = remaining.length > 0 ? remaining : undefined;
          }
          sendFailed = true;
          break;
        }
        // Small delay between chunks to avoid overwhelming Lambda concurrency limits on large pastes
        await new Promise(resolve => setTimeout(resolve, 25));
      }
      if (!sendFailed && eventsToSend.current) {
        const sent = new Set(eventsSnapshot || []);
        const remaining = eventsToSend.current.filter(e => !sent.has(e));
        eventsToSend.current = remaining.length > 0 ? remaining : undefined;
      }
      if (hadOversizedEvent) {
        toast({
          description: 'Some changes were too large to sync with other users in real time. Have any collaborators wait a minute and then refresh their page to get your latest edits.',
          variant: 'error'
        });
      }
    } else {
      const result = sendEvent({
        realm: 'distro',
        channel,
        event: 'change',
        data: {
          events: toSend,
          userActivity: myActivity,
          uuid: uuid1 || ''
        }
      });
      if (result?.status === 'success' && eventsToSend.current) {
        const sent = new Set(eventsSnapshot || []);
        const remaining = eventsToSend.current.filter(e => !sent.has(e));
        eventsToSend.current = remaining.length > 0 ? remaining : undefined;
      } else if (result?.status !== 'success') {
        sendFailed = true;
      }
    }

    // If send failed and there are still events to send, schedule a retry
    if (sendFailed && eventsToSend.current && eventsToSend.current.length > 0) {
      sendRetryTimer.current = window.setTimeout(() => {
        doSendRealtimeEvents(myActivity, uuid1);
      }, 1000);
    }
  }, [sendEvent, channel, toast]);

  const refreshBranches = useCallback(async () => {
    const updatedBranches = await getBranches({ survey: surveyName });
    if (!updatedBranches || !Array.isArray(updatedBranches)) return;
    setBranches(updatedBranches);
    return updatedBranches;
  }, [getBranches, surveyName]);

  const writeNewCollabVersion = useCallback(async (params: {
    toSend: string,
    publish?: boolean,
    commitMessage?: string,
    distroCommitId?: string,
    userActivity?: UserActivity,
    publishProgressKey?: string,
    action?: VersionAction,
  }) => {
    if (!branchId) {
      return {
        status: 'no branchId',
        error: '',
      };
    }

    const { toSend, publish, commitMessage, distroCommitId, userActivity, publishProgressKey } = params;
    const partSize = 5_000_000; // ~5MB
    // decide if the events toSend are too big to send over https. If so,
    // we use multipart upload to send them in chunks straight to s3.
    const numParts = Math.ceil(toSend.length / partSize);
    let saveRes: Awaited<ReturnType<typeof writeCollabVersion>>;
    if (numParts > 1) {
      const fileName = `${surveyName}/collabEvents.txt`; // Choose an appropriate file name
      const file = new File([toSend], fileName, { type: "text/plain" });

      const res = await multipartUpload.startUpload(file, {
        pathParams: {
          kind: 'collab',
          survey: surveyName
        },
        contentType: 'text/plain',
        length: toSend.length
      });

      saveRes = await writeCollabVersion({
        name: surveyName,
        branchId,
        events: '',
        locationURL: res?.location,
        publish,
        commitMessage,
        distroCommitId,
        userActivity,
        publishProgressKey,
        source,
        action: params.action ?? (publish ? 'publish' : 'bulk_write'),
      });
    } else {
      saveRes = await writeCollabVersion({
        name: surveyName,
        branchId,
        events: toSend,
        locationURL: '',
        publish,
        commitMessage,
        distroCommitId,
        userActivity,
        publishProgressKey,
        source,
        action: params.action ?? (publish ? 'publish' : 'bulk_write'),
      });
    }

    const toReturn = {
      status: saveRes.status || (saveRes.error ? 'err' : 'ok'),
      error: saveRes.error || '',
    };

    // Publishing changes parent_ops_version on the server — reset the watcher
    // and refresh branches so it doesn't fire a spurious "updated by another user" toast.
    if (publish && toReturn.status !== 'err') {
      lastParentOpsVersion.current = undefined;
      await refreshBranches();
    }
    if (toReturn.error === 'branch_behind_published') {
      await checkBehindPublished();
    }
    return toReturn;
  }, [surveyName, branchId, writeCollabVersion, multipartUpload, refreshBranches, checkBehindPublished]);

  const createBranch = useCallback(async (name: string) => {
    const result = await postCreateBranch({ name, survey: surveyName });
    if (result && 'error' in result) {
      return undefined;
    }
    await refreshBranches();
    return result;
  }, [postCreateBranch, surveyName, refreshBranches, toast]);

  const disableCollab = useCallback(async () => {
    setCollabEnabled(false);
    const result = await clearCollabEvents({ name: surveyName });

    if (result?.status !== 'ok') {
      setCollabEnabled(true);
      toast({
        description: result?.error || 'Failed to disable collab mode. Please try again.',
        variant: 'error'
      });
      return;
    }

    sendEvent({
      realm: 'distro',
      channel,
      event: 'disable_collab',
      data: {
        ...user, tabId
      }
    });
  }, [sendEvent, clearCollabEvents, user, tabId, channel, surveyName, toast]);

  // Attempts to save events to DynamoDB, returns true on success
  const attemptSaveEvents = useCallback(async (userActivity?: UserActivity) => {
    const currentEvents = eventsToSave.current;
    if (!currentEvents || currentEvents.length === 0) {
      return true; // Nothing to save
    }

    if (!branchId) {
      return false; // No branchId yet, try again when we have one
    }

    const uniqueEvents = removeDuplicateEvents(currentEvents);
    const toSend = JSON.stringify(uniqueEvents);
    if (debug) console.log('sending ', toSend);

    try {
      let saveSucceeded = false;
      // These collections of events can get big. If they're REALLY big, use writeNewVersion's multiupload
      // to upload them to s3 and save a new version automatically.
      if (toSend.length > 100000) {
        const result = await writeNewCollabVersion({
          toSend,
          userActivity
        });
        saveSucceeded = result.status === 'ok';
      } else {
        const result = await saveCollabEvents({
          name: surveyName,
          branchId,
          events: toSend,
          userActivity
        });
        saveSucceeded = result?.status === 'ok';
      }

      if (saveSucceeded) {
        if (eventsToSave.current) {
          const saved = new Set(currentEvents);
          const remaining = eventsToSave.current.filter(e => !saved.has(e));
          eventsToSave.current = remaining.length > 0 ? remaining : undefined;
        }
        consecutiveSaveFailures.current = 0;
        if (!eventsToSave.current || eventsToSave.current.length === 0) {
          setSendingEvents(false);
        }
        return true;
      }
    } catch (error) {
      console.error('Error saving collab events:', error);
    }

    // Save failed - increment failure count and maybe alert
    consecutiveSaveFailures.current += 1;
    if (consecutiveSaveFailures.current >= 3) {
      toast({
        description: 'Having trouble saving your changes. Please check your connection.',
        variant: 'error'
      });
      // Reset counter so we don't spam alerts
      consecutiveSaveFailures.current = 0;
    }
    return false;
  }, [writeNewCollabVersion, saveCollabEvents, surveyName, branchId, debug, toast]);

  // Fetch branch state from the server and reinitialize the local editor
  const fetchAndInitializeBranch = useCallback(async (targetBranchId: number): Promise<boolean> => {
    if (fetchingBranchRef.current) return false;
    fetchingBranchRef.current = true;
    try {
      const collabResp = await getCollabEvents({ name: surveyName, branchId: targetBranchId });
      if (collabResp && collabResp.collabEnabled) {
        setDebug(collabResp.debug);

        const { events: allEvents, unpublishedEvents } = await resolveCollabEvents(collabResp);
        const userActivity = Object.keys(collabResp.userActivity || {}).map(k => ({
          lastEditedObject: collabResp.userActivity[k].lastEditedObject,
          uid: collabResp.userActivity[k].uid,
          name: collabResp.userActivity[k].name,
          tabId: collabResp.userActivity[k].tabId,
        }));
        mergeReceivedEventsCallback(allEvents, userActivity, true, collabResp.debug, unpublishedEvents, !!collabResp.collabEnabled);
      }
      return true;
    } finally {
      fetchingBranchRef.current = false;
    }
  }, [surveyName, getCollabEvents, mergeReceivedEventsCallback, resolveCollabEvents]);

  // Detect when another user rebases or resets the current branch
  const lastParentOpsVersion = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!branchId || !branches) return;
    // Skip during branch switch or rebase — those operations already handle reinitialization
    if (isOperationInProgress()) {
      return;
    }
    const currentBranch = branches.find(b => b.id === branchId);
    if (!currentBranch) return;

    const version = currentBranch.parent_ops_version;
    if (lastParentOpsVersion.current === undefined) {
      // First load — just store the initial value
      lastParentOpsVersion.current = version;
      return;
    }
    if (lastParentOpsVersion.current !== version) {
      lastParentOpsVersion.current = version;
      fetchAndInitializeBranch(branchId);
      toast({
        description: 'Branch was updated by another user. Your view has been refreshed.',
        variant: 'default'
      });
    }
  }, [branchId, branches, fetchAndInitializeBranch, toast]);

  const switchBranch = useCallback(async (newBranchId: number) => {
    if (newBranchId === branchId) return;
    if (switchingBranchRef.current) return;

    switchingBranchRef.current = true;
    setSwitchingBranch(true);

    try {
      // Flush pending events for the current branch before navigating away
      await attemptSaveEvents();

      if (saveEventsTimer.current) clearTimeout(saveEventsTimer.current);
      if (saveRetryTimer.current) clearTimeout(saveRetryTimer.current);
      if (sendEventsTimer.current) clearTimeout(sendEventsTimer.current);
      if (sendRetryTimer.current) clearTimeout(sendRetryTimer.current);

      if (branchId) {
        await deleteUserActivityForBranch({ name: surveyName, branchId, tabId });
      }

      // Set (or clear) the branch query param and reload so the page initializes
      // fresh on the target branch, avoiding all in-place state juggling.
      const url = new URL(window.location.href);
      const targetIsDevBranch = branches?.find(b => b.id === newBranchId)?.is_dev_branch;
      if (targetIsDevBranch) {
        url.searchParams.delete('branch');
      } else {
        url.searchParams.set('branch', String(newBranchId));
      }
      window.location.assign(url.toString());
    } catch (e) {
      toast({
        description: 'Failed to switch branch',
        variant: 'error'
      });
      switchingBranchRef.current = false;
      setSwitchingBranch(false);
    }
  }, [branchId, surveyName, branches, tabId, attemptSaveEvents, deleteUserActivityForBranch, toast]);

  const restoreBranchVersion = useCallback(async (versionId: string) => {
    if (!branchId) {
      toast({
        description: 'No branch selected.',
        variant: 'error',
      });
      return;
    }

    setCollabEnabled(true);

    const result = await postRestoreBranchToCollabVersion({
      surveyName,
      branchId,
      versionId,
    });

    if (!result || result.success === false) {
      setCollabEnabled(false);
      const message =
        result && 'error' in result && result.error
          ? result.error
          : 'Failed to restore collab version. Please try again.';
      toast({
        description: message,
        variant: 'error',
      });
      return;
    }

    toast({
      description: 'Successfully restored version',
      variant: 'success',
    });
    sendEvent({
      realm: 'distro',
      channel,
      event: 'enable_collab',
      data: {
        ...user, tabId
      }
    });
    window.location.reload();
    return result;
  }, [sendEvent, postRestoreBranchToCollabVersion, surveyName, branchId, user, tabId, channel, toast]);

  const enableCollab = useCallback(async (survey: object) => {
    const result = await enableCollabReq({ name: surveyName, content: JSON.stringify(survey) });

    if (result?.status !== 'ok') {
      toast({
        description: result?.error || 'Failed to enable collab mode. Please try again.',
        variant: 'error',
      });
      return result;
    }

    sendEvent({
      realm: 'distro',
      channel,
      event: 'enable_collab',
      data: {
        ...user, tabId
      }
    });
    window.location.reload();
    return result;
  }, [enableCollabReq, surveyName, sendEvent, channel, user, tabId, toast]);

  // If current branch was deleted by another user, switch to default
  useEffect(() => {
    if (branchId && branches && branches.length > 0 && !branches.find(b => b.id === branchId)) {
      const devBranch = branches.find(b => b.is_dev_branch);
      if (devBranch) {
        toast({ description: 'Your branch was deleted. Switched to default.', variant: 'default' });
        switchBranch(devBranch.id);
      }
    }
  }, [branches, branchId, switchBranch, toast]);

  const deleteBranchAction = useCallback(async (branchIdToDelete: number) => {
    const result = await postDeleteBranch({ survey: surveyName, branchId: branchIdToDelete });
    if (!result || 'error' in result) {
      toast({
        description: result?.error || 'Failed to delete branch',
        variant: 'error'
      });
      return;
    }

    await refreshBranches();
    toast({
      description: 'Branch deleted',
      variant: 'success'
    });
  }, [surveyName, postDeleteBranch, refreshBranches, toast]);

  const resetBranchToPublished = useCallback(async () => {
    if (!branchId) return undefined;

    resettingRef.current = true;
    setResetting(true);
    try {
      const result = await postResetBranchToPublished({ survey: surveyName, branchId });
      if (!result || 'error' in result) {
        toast({
          description: result?.error || 'Failed to reset branch',
          variant: 'error'
        });
        return undefined;
      }

      setBehindPublished(false);
      lastParentOpsVersion.current = undefined;
      await fetchAndInitializeBranch(branchId);
      // Refresh branches so the watcher sees the new parent_ops_version
      // before resettingRef is cleared in finally
      await refreshBranches();

      toast({
        description: 'Branch reset to published version',
        variant: 'success'
      });

      return { success: true };
    } finally {
      resettingRef.current = false;
      setResetting(false);
    }
  }, [branchId, surveyName, postResetBranchToPublished, fetchAndInitializeBranch, refreshBranches, toast]);

  const rebaseBranch = useCallback(async (resolvedEvents?: ChangeSet, publishedVersionId?: string) => {
    if (!branchId) return undefined;

    rebasingRef.current = true;
    setRebasing(true);
    try {
      // Flush pending events so they're included in the backend merge
      await attemptSaveEvents();

      const result = await postRebaseBranch({ survey: surveyName, branchId, resolvedEvents, publishedVersionId });
      if (!result || 'error' in result) {
        toast({
          description: typeof result?.error === 'string' ? result.error : 'Failed to sync branch',
          variant: 'error'
        });
        return undefined;
      }

      if (!result.success && result.hasConflicts) {
        // Has conflicts — return the merge result for the UI to handle
        return result;
      }

      // Success — re-fetch and reinitialize branch state
      setBehindPublished(false);
      lastParentOpsVersion.current = undefined;
      await fetchAndInitializeBranch(branchId);
      await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      // Refresh branches so the watcher sees the new parent_ops_version
      // before rebasingRef is cleared in finally
      await refreshBranches();
      await checkBehindPublished();
      return { success: true, hasConflicts: false };
    } finally {
      rebasingRef.current = false;
      setRebasing(false);
    }
  }, [branchId, surveyName, postRebaseBranch, attemptSaveEvents, fetchAndInitializeBranch, refreshBranches, toast, checkBehindPublished]);

  // This function handles sending new collab events over websockets to other listening clients,
  // and saves new collab events to dynamo (or s3, if they're really large)
  const handleNewCollabEvents = useCallback(async (events: ChangeSet, myActivity?: UserActivity, state?: State) => {
    const uuid1 = uuidv4();
    const thisRoundEvents = (eventsToSave.current ?? []).concat(events);

    if (events.length > 0) {
      setSendingEvents(true);
    }

    // Cancel any pending save timers - this new debounce will include all pending events
    if (saveEventsTimer.current) clearTimeout(saveEventsTimer.current);
    if (saveRetryTimer.current) clearTimeout(saveRetryTimer.current);
    eventsToSave.current = thisRoundEvents;

    if (sendEventsTimer.current) clearTimeout(sendEventsTimer.current);
    if (sendRetryTimer.current) clearTimeout(sendRetryTimer.current);
    eventsToSend.current = (eventsToSend.current ?? []).concat(events);

    sendEventsTimer.current = window.setTimeout(() => {
      doSendRealtimeEvents(state ? state.myActivity : myActivity, uuid1);
    }, 100);

    const userActivity = state ? state.myActivity : myActivity;

    // We debounce 1 full second, then persist events to dynamo
    saveEventsTimer.current = window.setTimeout(async () => {
      const success = await attemptSaveEvents(userActivity);

      // If save failed and there are still events to save, schedule a retry,
      // This will get cleared if a new edit comes in before the retry happens.
      if (!success && eventsToSave.current && eventsToSave.current.length > 0) {
        saveRetryTimer.current = window.setTimeout(async () => {
          await attemptSaveEvents(userActivity);
        }, 3000);
      }
    }, 1000);
  }, [doSendRealtimeEvents, attemptSaveEvents]);

  useInterval(() => {
    if (collabEnabled && branchId && autosave && !paused) autoSaveCollabVersion({name: surveyName, branchId, source})
  }, 20 * 1000);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (collabEnabled && branchId && user.uid) {
        // Save any pending events that haven't been persisted yet
        const pendingEvents = eventsToSave.current ?? [];
        const uniqueEvents = removeDuplicateEvents(pendingEvents);

        saveCollabEvents({
          name: surveyName,
          branchId,
          events: JSON.stringify(uniqueEvents),
          userActivity: {
            uid: user.uid,
            name: config?.user?.name,
            tabId,
            lastEditedObject: ''
          }
        });

        // Send userActivity update over websockets too, so that existing distro viewers don't see you lingering.
        doSendRealtimeEvents({
          uid: user.uid,
          name: config?.user?.name,
          tabId,
          lastEditedObject: ''
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [doSendRealtimeEvents, saveCollabEvents, surveyName, tabId, config?.user?.name, user, collabEnabled]);

  const refreshBranchesIfIdle = useCallback(async () => {
    if (!isOperationInProgress()) {
      await refreshBranches();
      await checkBehindPublished();
    }
  }, [isOperationInProgress, refreshBranches, checkBehindPublished]);

  useAsyncInterval(async () => {
    if (!collabEnabled || paused) return;
    await refreshBranchesIfIdle();
  }, 3000);

  const collabBusy: CollabBusyState | undefined =
    rebasing ? 'rebasing' :
      resetting ? 'resetting' :
        switchingBranch ? 'switching' :
          undefined;

  /** Display name for the current branch (`default` for the dev branch, else branch name). */
  const branchName = useMemo(() => {
    if (!branches || !branchId) return 'branch';
    const b = branches.find(x => x.id === branchId);
    return b?.is_dev_branch ? 'default' : (b?.name ?? 'branch');
  }, [branches, branchId]);

  // https://react.dev/reference/react/useCallback#optimizing-a-custom-hook
  // "If you’re writing a custom Hook, it’s recommended to wrap any functions that it returns into useCallback"
  return {
    collabEnabled,
    collabReady,
    branchId,
    branchName,
    branches,
    canPublish,
    channel,
    socketConnected,
    alertJustPublished,
    getCollabState,
    handleNewCollabEvents,
    enableCollab,
    restoreBranchVersion,
    disableCollab,
    writeNewCollabVersion,
    createBranch,
    refreshBranches,
    fetchPublishedChanges,
    switchBranch,
    switchingBranch,
    collabBusy,
    fetchAndInitializeBranch,
    rebasing,
    rebaseBranch,
    resetting,
    setContextOperationInProgress,
    deleteBranch: deleteBranchAction,
    resetBranchToPublished,
    behindPublished,
    parentStateRef,
    parentOpsRef,
  };
}
